// The app's state and its pure reducer. Nothing here touches the network, storage or the DOM, so
// every state in SPEC 7.3 can be reached in a test by dispatching actions.
import type { Agent, Card, CardStatus, Choice, Command, DaemonMessage, MachineInfo, Member, PublicIdentity, Question, ResultStage, Settings, Stamp } from "@whistle/protocol";
import { displayBlock, displayLine, displayText } from "./sanitize";

export type PairingRecord = {
  accountId: string;
  machineId: string;
  machine: PublicIdentity & { name: string };
  relayOrigin: string;
  deviceId: string;
  deviceName: string;
  pairedAt: number;
};

/** The relay connection, from the phone's point of view. "removed" means the relay no longer knows this device. */
export type ConnStatus = "idle" | "connecting" | "connected" | "reconnecting" | "offline" | "removed";

export type SendStage = ResultStage | "sending" | "not-sent";

/** One answer sent (or not) from this phone; the source of Recent. */
export type SendRecord = {
  commandId: string;
  requestId: string;
  promptHash: string;
  agentName: string;
  agentKind: string;
  machineName: string;
  envLabel: string;
  project?: string;
  /** The choice as it was labelled: "Allow once", "Deny and explain", "2 answers". */
  summary: string;
  /**
   * What the answer did, taken from the command that was built. Records saved before 24 Oct 2026 have none, and
   * recordEffect reads it from `summary` instead.
   */
  effect?: AnswerEffect;
  /** The card's title when the answer was sent. Absent on records saved before 24 Oct 2026. */
  title?: string;
  /** A few words for what the request concerned ("Write v309.txt"), from requestSummary. Absent on older records. */
  request?: string;
  /** Typed reply text. Shown for 24 hours, then only "typed reply sent" (SPEC 5.5). */
  text?: string;
  startedAt: number;
  updatedAt: number;
  stage: SendStage;
  reason?: string;
  /** The daemon's results carried reason "turn-ended": it ran, but Codex had finished its turn and won't see it. */
  turnEnded?: boolean;
};

/** "update": the daemon says a new release is out, once per release. */
export type NoticeKind = "device-added" | "device-removed" | "setting-changed" | "integration" | "update";
export type Notice = { id: string; kind: NoticeKind; text: string; at: number };

export type ThemePref = "system" | "light" | "dark";
export type Prefs = { theme: ThemePref; haptics: boolean; finishedOpen: boolean };

export type PushHealth = {
  supported: boolean;
  permission: "default" | "granted" | "denied";
  subscribed: boolean;
  registered: boolean; // the relay confirmed registerPush this session
  lastError?: string;
};

export type AppState = {
  booted: boolean;
  pairing: PairingRecord | null;
  conn: ConnStatus;
  online: boolean;
  machineOnline: boolean | null;
  machineLastSeen?: number;
  machine?: MachineInfo;
  agents: Agent[];
  cards: Card[];
  settings?: Settings;
  stamp?: Stamp;
  /** When the last `state` arrived, on the phone's clock. */
  stateAt?: number;
  /** requestId -> when its prompt last changed while on screen (drives the 1 s "Updated" lock). */
  updatedCards: Record<string, number>;
  /** An older daemon's screen cards that replaced a hook card for the same agent: "Moved to your desk" (SPEC 3.11). */
  releasedCards: Record<string, true>;
  sends: Record<string, SendRecord>;
  /** The last "Not sent" note per request, shown on the card until the user answers again. */
  notSent: Record<string, { reason: string; at: number }>;
  notices: Notice[];
  removed: boolean;
  /**
   * The machine itself said so, in a signed notice naming this phone's deviceId. Only then does the phone delete what
   * it stores for the pairing. The relay's word (close 4003, or 4001 twice) only empties the screen: a relay can send
   * those at will, and must not be able to un-pair the phone.
   */
  removedByMachine: boolean;
  push: PushHealth;
  prefs: Prefs;
};

export const DEFAULT_PREFS: Prefs = { theme: "system", haptics: true, finishedOpen: false };

export const DEFAULT_PUSH: PushHealth = { supported: false, permission: "default", subscribed: false, registered: false };

export const initialState: AppState = {
  booted: false,
  pairing: null,
  conn: "idle",
  online: true,
  machineOnline: null,
  agents: [],
  cards: [],
  updatedCards: {},
  releasedCards: {},
  sends: {},
  notSent: {},
  notices: [],
  removed: false,
  removedByMachine: false,
  push: DEFAULT_PUSH,
  prefs: DEFAULT_PREFS,
};

export type Action =
  | { type: "boot"; pairing: PairingRecord | null; recent: SendRecord[]; prefs: Partial<Prefs>; push: PushHealth; online: boolean; at?: number }
  | { type: "paired"; pairing: PairingRecord }
  | { type: "unpaired" }
  | { type: "conn"; status: ConnStatus }
  | { type: "online"; online: boolean }
  | { type: "ready"; members: Member[]; at: number }
  | { type: "presence"; id: string; online: boolean; lastSeen: number }
  | { type: "daemon"; msg: DaemonMessage; at: number }
  | { type: "sendStarted"; record: SendRecord }
  | { type: "sendStage"; commandId: string; stage: SendStage; reason?: string; at: number }
  | { type: "notSent"; requestId: string; reason: string; at: number }
  | { type: "clearNotSent"; requestId: string }
  | { type: "dismissNotice"; id: string }
  | { type: "prefs"; prefs: Partial<Prefs> }
  | { type: "push"; push: Partial<PushHealth> }
  | { type: "removed"; byMachine?: boolean }
  | { type: "forgetUpdated"; requestId: string };

const RECENT_KEEP = 60;
const RECENT_MAX_AGE_MS = 7 * 24 * 60 * 60_000;

function pruneSends(sends: Record<string, SendRecord>, now: number): Record<string, SendRecord> {
  const list = Object.values(sends)
    .filter((s) => now - s.startedAt < RECENT_MAX_AGE_MS)
    .sort((a, b) => b.startedAt - a.startedAt)
    .slice(0, RECENT_KEEP);
  return Object.fromEntries(list.map((s) => [s.commandId, s]));
}

/**
 * Recent as it may stay on the phone (security review 2, S2): at most 60 answers from the last 7 days, and typed text
 * only for its 24 hours. The controller writes this back, so what is dropped here leaves IndexedDB too.
 */
export function retainSends(recent: SendRecord[], now: number): Record<string, SendRecord> {
  const kept = pruneSends(Object.fromEntries(recent.map((r) => [r.commandId, r])), now);
  for (const [id, rec] of Object.entries(kept)) {
    const clean = cleanRecord(rec);
    if (clean.text !== undefined && now - clean.startedAt >= TEXT_VISIBLE_MS) delete clean.text;
    kept[id] = clean;
  }
  return kept;
}

export function reduce(state: AppState, action: Action): AppState {
  switch (action.type) {
    case "boot": {
      const sends = retainSends(action.recent, action.at ?? Date.now());
      return { ...state, booted: true, pairing: cleanPairing(action.pairing), sends, prefs: { ...DEFAULT_PREFS, ...action.prefs }, push: action.push, online: action.online };
    }
    case "paired":
      return { ...state, pairing: cleanPairing(action.pairing), removed: false, removedByMachine: false, conn: "idle", machineOnline: null };
    case "unpaired":
      return { ...initialState, booted: true, prefs: state.prefs, push: state.push, online: state.online };
    case "conn": {
      // Only pairing again leaves "removed"; a socket closing after the removal doesn't.
      if (state.removed) return state;
      const next: AppState = { ...state, conn: action.status };
      if (action.status !== "connected") next.machineOnline = state.machineOnline === null ? null : false;
      return next;
    }
    case "online":
      return { ...state, online: action.online, conn: action.online ? state.conn : "offline" };
    case "ready": {
      const m = action.members.find((x) => x.id === state.pairing?.machineId);
      return { ...state, conn: "connected", machineOnline: m ? m.online : false, machineLastSeen: m?.lastSeen ?? state.machineLastSeen };
    }
    case "presence": {
      if (action.id !== state.pairing?.machineId) return state;
      return { ...state, machineOnline: action.online, machineLastSeen: action.lastSeen };
    }
    case "daemon":
      return applyDaemon(state, action.msg, action.at);
    case "sendStarted":
      return { ...state, sends: pruneSends({ ...state.sends, [action.record.commandId]: action.record }, action.record.startedAt), notSent: without(state.notSent, action.record.requestId) };
    case "sendStage": {
      const rec = state.sends[action.commandId];
      if (!rec) return state;
      // Results only move forward along accepted -> handed -> continued; a late "accepted" must not undo "handed".
      if (rank(action.stage) < rank(rec.stage) && isProgress(action.stage) && isProgress(rec.stage)) return state;
      const next: SendRecord = { ...rec, stage: action.stage, updatedAt: action.at };
      // "turn-ended" is a code, not words for the screen: it becomes a flag and leaves `reason` to human text.
      if (action.reason === TURN_ENDED && isProgress(action.stage)) next.turnEnded = true;
      else next.reason = action.reason ?? rec.reason;
      return { ...state, sends: { ...state.sends, [action.commandId]: next } };
    }
    case "notSent":
      if (state.removed) return state; // a hold cancelled by the removal itself
      return { ...state, notSent: { ...state.notSent, [action.requestId]: { reason: action.reason, at: action.at } } };
    case "clearNotSent":
      return { ...state, notSent: without(state.notSent, action.requestId) };
    case "dismissNotice":
      return { ...state, notices: state.notices.filter((n) => n.id !== action.id) };
    case "prefs":
      return { ...state, prefs: { ...state.prefs, ...action.prefs } };
    case "push":
      return { ...state, push: { ...state.push, ...action.push } };
    case "removed":
      if (state.removed) return action.byMachine && !state.removedByMachine ? { ...state, removedByMachine: true } : state;
      // Nothing given or typed for this machine stays on screen (SPEC "Phone storage"). The pairing stays in memory, to
      // say which machine removed the phone; what leaves storage is the controller's call, on removedByMachine.
      return { ...state, removed: true, removedByMachine: !!action.byMachine, conn: "removed", machineOnline: false, cards: [], agents: [], sends: {}, notSent: {}, updatedCards: {}, releasedCards: {}, stamp: undefined, stateAt: undefined };
    case "forgetUpdated":
      return { ...state, updatedCards: without(state.updatedCards, action.requestId) };
  }
}

const PROGRESS: SendStage[] = ["sending", "accepted", "handed", "continued"];
const rank = (s: SendStage) => PROGRESS.indexOf(s);
const isProgress = (s: SendStage) => PROGRESS.includes(s);

function without<T>(rec: Record<string, T>, key: string): Record<string, T> {
  if (!(key in rec)) return rec;
  const copy = { ...rec };
  delete copy[key];
  return copy;
}

function applyDaemon(state: AppState, raw: DaemonMessage, at: number): AppState {
  // The machine's notice can be opened after the relay has already closed the socket, so it still counts here.
  if (state.removed) return removalOf(raw, state.pairing) === "by-id" ? reduce(state, { type: "removed", byMachine: true }) : state;
  const msg = cleanMessage(raw);
  switch (msg.type) {
    case "paired":
      return state; // handled by the pairing flow, which dispatches "paired" with the full record
    case "state": {
      const prevById = new Map(state.cards.map((c) => [c.requestId, c]));
      const updatedCards = { ...state.updatedCards };
      const releasedCards: Record<string, true> = {};
      const prevHookByAgent = new Map(state.cards.filter((c) => c.source === "hook").map((c) => [c.agentId, c]));
      for (const card of msg.cards) {
        const prev = prevById.get(card.requestId);
        if (prev && prev.promptHash !== card.promptHash) updatedCards[card.requestId] = at;
        if (state.releasedCards[card.requestId]) releasedCards[card.requestId] = true;
        // A newer daemon says so itself, in the screen card's note.
        if (fromOlderDaemon(card) && card.source === "screen" && (card.kind === "desk" || card.kind === "maybe")) {
          const hook = prevHookByAgent.get(card.agentId);
          if (hook && hook.requestId !== card.requestId && !msg.cards.some((c) => c.requestId === hook.requestId)) releasedCards[card.requestId] = true;
        }
      }
      for (const id of Object.keys(updatedCards)) if (!msg.cards.some((c) => c.requestId === id)) delete updatedCards[id];
      // A send still in flight whose question changed under it is reported honestly, without waiting for the daemon.
      const sends = { ...state.sends };
      for (const s of Object.values(sends)) {
        if (s.stage !== "sending") continue;
        const card = msg.cards.find((c) => c.requestId === s.requestId);
        if (card && card.promptHash !== s.promptHash) sends[s.commandId] = { ...s, stage: "changed", reason: "The question changed.", updatedAt: at };
      }
      return {
        ...state,
        machine: msg.machine,
        agents: msg.agents,
        cards: msg.cards,
        settings: msg.settings,
        stamp: msg.stamp,
        stateAt: at,
        updatedCards,
        releasedCards,
        sends,
        machineOnline: true,
        machineLastSeen: at,
      };
    }
    case "result":
      return reduce(state, { type: "sendStage", commandId: msg.commandId, stage: msg.stage, reason: msg.reason, at });
    case "notice": {
      const notice: Notice = { id: `${msg.at}-${msg.kind}-${state.notices.length}`, kind: msg.kind, text: msg.text, at: msg.at };
      const next = { ...state, notices: [notice, ...state.notices].slice(0, 20) };
      const removal = removalOf(msg, state.pairing);
      if (removal) return reduce(next, { type: "removed", byMachine: removal === "by-id" });
      return next;
    }
    case "alert":
      return state; // the service worker's business
    default:
      return state;
  }
}

/**
 * Whether a device-removed notice removes this phone. Notices are machine-signed, so one naming this phone's deviceId
 * is an authenticated removal ("by-id"). One that says "this phone" only in words is shown as a removal but proves
 * nothing about which phone, so it deletes nothing ("in-words").
 */
function removalOf(msg: DaemonMessage, pairing: PairingRecord | null): "by-id" | "in-words" | null {
  if (msg.type !== "notice" || msg.kind !== "device-removed" || !pairing) return null;
  const deviceId = (msg as { deviceId?: unknown }).deviceId;
  if (typeof deviceId === "string") return deviceId === pairing.deviceId ? "by-id" : null;
  return typeof msg.text === "string" && /this phone|this device/i.test(msg.text) ? "in-words" : null;
}

// ---------- Display text from the daemon (security review 2, S3) ----------
// Every string the daemon supplies for display is cleaned once, as it arrives, so no screen can forget to. Ids, hashes
// and `detail` are left alone: commands carry the ids back, and `detail` is shown through CommandText, which cleans it
// and marks what it removed. The machine's name in the pairing record came from the relay, so it is cleaned as well.

const optLine = (s: string | undefined, max?: number) => (s === undefined ? undefined : displayLine(s, max));
const optBlock = (s: string | undefined, max?: number) => (s === undefined ? undefined : displayBlock(s, max));

function cleanQuestion(q: Question): Question {
  return {
    ...q,
    text: displayBlock(q.text, 1000),
    header: optLine(q.header, 60),
    options: Array.isArray(q.options) ? q.options.map((o) => ({ ...o, label: displayLine(o.label, 200), description: optBlock(o.description, 500) })) : q.options,
  };
}

function cleanCard(c: Card): Card {
  return {
    ...c,
    agentName: displayLine(c.agentName, 80),
    agentKind: displayLine(c.agentKind, 40),
    envLabel: displayLine(c.envLabel, 80),
    project: optLine(c.project, 160),
    title: displayLine(c.title, 300),
    note: optLine(c.note, 300),
    choices: Array.isArray(c.choices) ? c.choices.map((ch) => ({ ...ch, label: displayLine(ch.label, 200), keyHint: optLine(ch.keyHint, 16) })) : c.choices,
    questions: Array.isArray(c.questions) ? c.questions.map(cleanQuestion) : c.questions,
  };
}

function cleanMessage(msg: DaemonMessage): DaemonMessage {
  switch (msg.type) {
    case "state":
      return {
        ...msg,
        machine: msg.machine && { ...msg.machine, name: displayLine(msg.machine.name, 80), envs: Array.isArray(msg.machine.envs) ? msg.machine.envs.map((e) => ({ ...e, label: displayLine(e.label, 80) })) : msg.machine.envs },
        agents: Array.isArray(msg.agents) ? msg.agents.map((a) => ({ ...a, name: displayLine(a.name, 80), kind: displayLine(a.kind, 40), env: displayLine(a.env, 80), project: optLine(a.project, 160) })) : msg.agents,
        cards: Array.isArray(msg.cards) ? msg.cards.map(cleanCard) : msg.cards,
      };
    case "result":
      // "turn-ended" is a code the reducer reads, not text.
      return msg.reason === undefined || msg.reason === TURN_ENDED ? msg : { ...msg, reason: displayBlock(msg.reason, 500) };
    case "notice":
      return { ...msg, text: displayBlock(msg.text, 500) };
    default:
      return msg;
  }
}

function cleanPairing(p: PairingRecord | null): PairingRecord | null {
  return p && { ...p, machine: { ...p.machine, name: displayLine(p.machine.name, 80) } };
}

/** Recent is rebuilt from IndexedDB, which may hold records saved before cards were cleaned. */
function cleanRecord(r: SendRecord): SendRecord {
  return {
    ...r,
    agentName: displayLine(r.agentName, 80),
    agentKind: displayLine(r.agentKind, 40),
    machineName: displayLine(r.machineName, 80),
    envLabel: displayLine(r.envLabel, 80),
    project: optLine(r.project, 160),
    summary: displayLine(r.summary, 200),
    effect: isEffect(r.effect) ? r.effect : undefined,
    title: optLine(r.title, 300),
    request: optLine(r.request, REQUEST_SUMMARY_MAX),
    reason: optBlock(r.reason, 500),
  };
}

// ---------- Selectors ----------

/**
 * Where a card stands, from the daemon's own fields (protocol "Cards: answerable, status and note", 21 Oct 2026). The
 * phone never reads answerability from the wording of `detail`, except for a card from an older daemon (legacyView).
 */
export type CardView = { answerable: boolean; status?: CardStatus; note?: string; request?: string };

/** A daemon from before 21 Oct 2026 sends no `answerable`, `status` or `note`. */
export function fromOlderDaemon(card: Card): boolean {
  return typeof (card as Partial<Card>).answerable !== "boolean";
}

export function cardView(card: Card): CardView {
  if (fromOlderDaemon(card)) return legacyView(card);
  const prompt = card.kind === "approval" || card.kind === "question";
  // The flag, and something to answer with. Any status other than open is the daemon saying no as well.
  const offered = card.kind === "approval" ? card.choices.length > 0 : !!card.questions?.length;
  const answerable = prompt && card.answerable && offered && (card.status === undefined || card.status === "open");
  let status: CardStatus | undefined;
  if (card.kind !== "finished") {
    status = card.status ?? (card.kind === "desk" || card.kind === "maybe" ? "desk" : "waiting");
    if (answerable) status = "open";
    else if (status === "open") status = "waiting";
  }
  return { answerable, status, note: card.note, request: withoutNote(card.detail, card.note) };
}

/**
 * The daemon also appends `note` to `detail` as its last line, for apps from before 21 Oct. Only that exact line comes
 * off, which leaves the daemon's own detail; "Show full request" always has the original.
 */
function withoutNote(detail: string | undefined, note: string | undefined): string | undefined {
  if (!detail || !note) return detail;
  if (detail === note) return undefined;
  return detail.endsWith(`\n${note}`) ? detail.slice(0, -(note.length + 1)) : detail;
}

// An older daemon's cards only. It lists a hook card it no longer takes an answer for with no choices and a note as the
// last line of `detail`. Question cards never carry choices, so for them the note is the only sign. A card being
// answered (reserved, dispatched) carries no note at all.
const DESK_NOTE = /^(?:Moved to your desk\b.*|Not sure it arrived\b.*|This one needs your desk\.)$/;
const WAIT_NOTE = /^Waiting for herdr to see the dialog on screen\.$/;

function legacyView(card: Card): CardView {
  if (card.kind !== "approval" && card.kind !== "question") return { answerable: false, status: card.kind === "finished" ? undefined : "desk", request: card.detail };
  const last = card.detail ? card.detail.slice(card.detail.lastIndexOf("\n") + 1).trim() : "";
  const desk = DESK_NOTE.test(last);
  const note = desk || WAIT_NOTE.test(last) ? last : undefined;
  const answerable = card.kind === "approval" ? card.choices.length > 0 : !!card.questions?.length && note === undefined;
  // An answerable card's detail is what Allow authorises: never trimmed, even if its last line looks like a note.
  if (answerable) return { answerable, status: "open", request: card.detail };
  const cut = card.detail?.lastIndexOf("\n") ?? -1;
  const request = note === undefined ? card.detail : cut < 0 ? undefined : card.detail!.slice(0, cut);
  return { answerable, status: desk ? "desk" : "waiting", note: optLine(note, 300), request };
}

/** For a prompt the daemon isn't taking an answer for and gave no reason: neutral, since that usually passes. */
export const NOT_OPEN_NOTE = "Your PC isn't taking an answer for this right now.";

/** A Codex approval still held after Codex ended its turn (`card.turnEnded`): answering works, with a catch. */
export const TURN_ENDED_TITLE = "Codex has finished its turn.";
export const TURN_ENDED_BODY = "Allowing runs it, but Codex won't see the result until you ask it to continue at your desk.";
/** The `reason` code the daemon puts on accepted, handed and continued for such a card. Never shown as text. */
export const TURN_ENDED = "turn-ended";

/** Whether this phone can answer the card at all. Only these are "needs an answer", in amber, and counted. */
export function isAnswerable(card: Card): boolean {
  return cardView(card).answerable;
}

/** A send from this phone for this exact question that hasn't finished: the card's answer is on its way. */
export function isInFlight(sends: Record<string, SendRecord>, card: Card): boolean {
  return Object.values(sends).some((s) => s.requestId === card.requestId && s.promptHash === card.promptHash && (s.stage === "sending" || s.stage === "accepted" || s.stage === "handed"));
}

/** An approval or question card the phone can't answer: released, in flight, not open yet, or desk-only. */
export function isReadOnlyPrompt(card: Card): boolean {
  return (card.kind === "approval" || card.kind === "question") && !isAnswerable(card);
}

/** A read-only prompt only the desk can answer now: released, or not sure it arrived. */
export function isAtDesk(card: Card): boolean {
  return isReadOnlyPrompt(card) && cardView(card).status === "desk";
}

/**
 * Where an approval or question stands for this phone: "answer" (it can be answered now), "sent" (an answer is on its
 * way), "desk" (only the desk can answer it now), or "pending" (not open for an answer at the moment).
 * Null for the other kinds of card.
 */
export type PromptState = "answer" | "sent" | "desk" | "pending";

export function promptState(card: Card, sends: Record<string, SendRecord> = {}): PromptState | null {
  if (card.kind !== "approval" && card.kind !== "question") return null;
  const v = cardView(card);
  // The daemon handing the card to the desk wins over an answer this phone still has marked in flight.
  if (v.status === "desk") return "desk";
  if (v.status === "sent" || isInFlight(sends, card)) return "sent";
  return v.answerable ? "answer" : "pending";
}

/** Why a card can't be answered here, in the daemon's words. Null when it can, or when an answer is on its way. */
export function readOnlyNote(card: Card): string | null {
  const v = cardView(card);
  if (v.answerable || card.kind === "finished" || v.status === "sent") return null;
  if (v.note) return v.note;
  return card.kind === "approval" || card.kind === "question" ? NOT_OPEN_NOTE : null;
}

/** The detail to show as the request: without the daemon's note, which the card shows as a note instead. */
export function requestText(card: Card): string | undefined {
  return cardView(card).request;
}

/** The turn-ended note belongs before answering: on a card that is open, or waiting to open again. */
export function showsTurnEnded(card: Card, sends: Record<string, SendRecord> = {}): boolean {
  const p = promptState(card, sends);
  return !!card.turnEnded && (p === "answer" || p === "pending");
}

/** needs: answerable now. pending: an answer on its way, or not open yet. desk: only the desk can answer. */
export type CardGroups = { needs: Card[]; pending: Card[]; desk: Card[]; finished: Card[] };

export function groupCards(cards: Card[], sends: Record<string, SendRecord> = {}): CardGroups {
  const byAge = (a: Card, b: Card) => a.createdAt - b.createdAt;
  const state = (c: Card) => promptState(c, sends);
  return {
    needs: cards.filter((c) => state(c) === "answer").sort(byAge),
    pending: cards.filter((c) => state(c) === "sent" || state(c) === "pending").sort(byAge),
    desk: cards.filter((c) => c.kind === "desk" || c.kind === "maybe" || state(c) === "desk").sort(byAge),
    finished: cards.filter((c) => c.kind === "finished").sort((a, b) => b.createdAt - a.createdAt),
  };
}

/** The number behind the tab badge, the page title and every "N need an answer". */
export function needsCount(cards: Card[], sends: Record<string, SendRecord> = {}): number {
  return cards.filter((c) => promptState(c, sends) === "answer").length;
}

export type Blocker =
  | { code: "offline"; text: string }
  | { code: "disconnected"; text: string }
  | { code: "unreachable"; text: string }
  | { code: "paused"; text: string }
  | { code: "removed"; text: string }
  | { code: "read-only"; text: string }
  | { code: "stale"; text: string }
  | { code: "updated"; text: string }
  | { code: "in-flight"; text: string };

/** Why a card cannot be answered right now, or null when it can. Mirrors the daemon's own rules so the phone never promises what the PC will refuse. */
export function answerBlocker(state: AppState, card: Card, now: number): Blocker | null {
  const machine = state.machine?.name ?? state.pairing?.machine.name ?? "your PC";
  if (state.removed) return { code: "removed", text: "This phone was removed. Pair again from your desk." };
  if (!state.online) return { code: "offline", text: "You're offline." };
  if (state.conn !== "connected") return { code: "disconnected", text: "Not connected. Reconnecting." };
  if (state.machineOnline === false) return { code: "unreachable", text: `${machine} unreachable.` };
  // Before read-only: a card with an answer on its way isn't answerable either, and says so in its result instead.
  if (promptState(card, state.sends) === "sent") return { code: "in-flight", text: "Answer sent. Waiting for the agent." };
  const v = cardView(card);
  // The daemon sends a paused machine's cards as waiting; the pause is the reason. A card at the desk keeps its own.
  if (!v.answerable && (v.status === "desk" || !state.settings?.remotePaused)) return { code: "read-only", text: readOnlyNote(card) ?? "This one needs your desk." };
  if (state.settings?.remotePaused) return { code: "paused", text: `Answers paused at ${machine}.` };
  if (!state.stamp || !state.stateAt || now - state.stateAt > 55_000) return { code: "stale", text: "Checking the question is still waiting." };
  const changed = state.updatedCards[card.requestId];
  if (changed && now - changed < 1000) return { code: "updated", text: "Updated" };
  return null;
}

export function sendsForCard(state: AppState, requestId: string): SendRecord[] {
  return Object.values(state.sends)
    .filter((s) => s.requestId === requestId)
    .sort((a, b) => b.startedAt - a.startedAt);
}

export function recentList(state: AppState): SendRecord[] {
  return Object.values(state.sends).sort((a, b) => b.startedAt - a.startedAt);
}

export const TEXT_VISIBLE_MS = 24 * 60 * 60_000;

/** Typed text is shown for 24 hours; afterwards Recent says only that a typed reply was sent (SPEC 5.5). */
export function visibleText(rec: SendRecord, now: number): string | null {
  if (!rec.text) return null;
  return now - rec.startedAt < TEXT_VISIBLE_MS ? rec.text : null;
}

// ---------- What an answer does (live test, 24 Oct 2026) ----------
// A Deny was sent where Allow was meant, from two choices that looked the same, and Recent then didn't make clear which
// was which. Every choice, the hold banner and Recent now carry the effect as a symbol, a tint and a word.

/** allow and deny are the choices' own effects; any other choice, and every set of answers, is "answer". */
export type AnswerEffect = "allow" | "deny" | "answer";

function isEffect(v: unknown): v is AnswerEffect {
  return v === "allow" || v === "deny" || v === "answer";
}

export function choiceEffect(effect: Choice["effect"] | undefined): AnswerEffect {
  return effect === "allow" || effect === "deny" ? effect : "answer";
}

/** The effect of the command actually built, read from the card's own choice, so the record says what was sent. */
export function commandEffect(command: Command, card: Card): AnswerEffect {
  const action = command.action;
  if (action.kind !== "choice") return "answer";
  return choiceEffect(card.choices.find((c) => c.id === action.choiceId)?.effect);
}

/**
 * A record's effect. Records from before 24 Oct 2026 carry only the label, so it is read from that, and only where the
 * label says it outright ("Allow once", "Deny", "Deny and explain"). Anything else stays a neutral "answer".
 */
export function recordEffect(rec: Pick<SendRecord, "effect" | "summary">): AnswerEffect {
  if (isEffect(rec.effect)) return rec.effect;
  if (/^allow\b/i.test(rec.summary)) return "allow";
  if (/^deny\b/i.test(rec.summary)) return "deny";
  return "answer";
}

export const REQUEST_SUMMARY_MAX = 60;

/** Lines of a request that say why or where rather than what: widening flags, the reason, the folder, other fields. */
const SIDE_LINE = /^(?:\[!\]|Reason: |In: |Also in the request: )/;
/** A line that is only a marker, like "[The command was cut for display; …]" or "[1 invisible character removed]". */
const MARKER_LINE = /^\[[^\]]*\]$/;
const PATCH_LINE = /^\*\*\* (Add|Update|Delete) File: (.+)$/;

const baseName = (path: string) => path.split(/[\\/]/).filter(Boolean).pop() ?? path;

/** The tool's name, from the first line of a hook card's full request ("Write\n{…}"); only if it looks like one. */
function toolName(card: Card): string | null {
  if (card.source !== "hook" || !card.detailFull) return null;
  const nl = card.detailFull.indexOf("\n");
  const first = (nl < 0 ? card.detailFull : card.detailFull.slice(0, nl)).trim();
  return /^[A-Za-z][\w.:-]{0,63}$/.test(first) ? first : null;
}

/**
 * A few words for what a request concerned, kept with the answer so Recent can say "You allowed: Write v309.txt".
 * Built from the display text (cleaned, with credentials already masked by the daemon), never from the original.
 */
export function requestSummary(card: Card): string | undefined {
  if (card.kind === "question" && card.questions?.length) {
    const [first, ...rest] = card.questions;
    const text = displayLine(first!.text, REQUEST_SUMMARY_MAX);
    return rest.length ? `${text} (+${rest.length} more)` : text;
  }
  const lines = displayText(requestText(card))
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  for (const line of lines) {
    const patch = PATCH_LINE.exec(line);
    if (patch) return displayLine(`${patch[1]} ${baseName(patch[2]!)}`, REQUEST_SUMMARY_MAX);
  }
  const main = lines.find((l) => !SIDE_LINE.test(l) && !MARKER_LINE.test(l));
  if (main) {
    const file = /^File: (.+)$/.exec(main);
    if (file) return displayLine(`${toolName(card) ?? "Change"} ${baseName(file[1]!)}`, REQUEST_SUMMARY_MAX);
    const url = /^URL: (.+)$/.exec(main);
    if (url) return displayLine(`${toolName(card) ?? "Open"} ${url[1]}`, REQUEST_SUMMARY_MAX);
    return displayLine(main, REQUEST_SUMMARY_MAX);
  }
  return card.title ? displayLine(card.title, REQUEST_SUMMARY_MAX) : undefined;
}

/** Stages where the answer never took effect: nothing left the phone, or the PC turned it down. */
const NOT_DELIVERED: readonly SendStage[] = ["not-sent", "changed", "expired", "refused"];

const DID: Record<AnswerEffect, string> = { allow: "You allowed", deny: "You denied", answer: "You answered" };
const TRIED: Record<AnswerEffect, string> = { allow: "You tried to allow", deny: "You tried to deny", answer: "You tried to answer" };

/**
 * What Recent's mark shows. The answer's effect once the PC has confirmed it took it, or turned it down; while it is
 * still on its way ("sending"), or nobody can say whether it arrived ("unknown"), that instead, so an answer that may
 * not have happened never wears the allow or deny mark.
 */
export type RecentMark = AnswerEffect | "sending" | "unknown";

export function recentMark(rec: Pick<SendRecord, "effect" | "summary" | "stage">): RecentMark {
  if (rec.stage === "sending" || rec.stage === "unknown") return rec.stage;
  return recordEffect(rec);
}

/**
 * Recent's first line: what was sent, and to what. "You allowed: Write v309.txt", "You denied: npm test", or for an
 * answer that never took effect "You tried to deny: …". Before it is confirmed it says "Sending: …", or "Not sure it
 * arrived: …" when that can't be known; the line under it gives the label. A record from before 24 Oct 2026 has no
 * request, so it says only "You denied".
 */
export function recentHeadline(rec: SendRecord): string {
  const effect = recordEffect(rec);
  const verb = rec.stage === "sending" ? "Sending" : rec.stage === "unknown" ? "Not sure it arrived" : (NOT_DELIVERED.includes(rec.stage) ? TRIED : DID)[effect];
  const what = rec.request || (rec.title ? rec.title.replace(/^(?:Allow|Apply|Approve|Run)\s+/i, "").replace(/\?$/, "") : "");
  return what ? `${verb}: ${what}` : verb;
}

export function agentCounts(agents: Agent[]): { working: number; blocked: number; idle: number; done: number; unknown: number } {
  const c = { working: 0, blocked: 0, idle: 0, done: 0, unknown: 0 };
  for (const a of agents) c[a.status]++;
  return c;
}

export type MachineStatus = "online" | "unreachable" | "unknown";
export function machineStatus(state: AppState): MachineStatus {
  if (state.conn !== "connected") return state.machineOnline === null ? "unknown" : "unreachable";
  return state.machineOnline ? "online" : "unreachable";
}
